import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Upload, FileText, Download, Loader2, CheckCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import * as pdfjsLib from 'pdfjs-dist';

interface InvestmentTermSheet {
  investors: string;
  companyOverview: string;
  investmentTerms: string;
  governance: string;
  closingConditions: string;
  signatureLines: string;
}

export const VentureSOWGenerator = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [termSheet, setTermSheet] = useState<InvestmentTermSheet | null>(null);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const extractTextFromPDF = async (file: File): Promise<string> => {
    try {
      console.log('Starting PDF text extraction for:', file.name);
      
      // PDF.js worker (v4+ uses .mjs, track installed version)
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
      
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      console.log('PDF loaded, pages:', pdf.numPages);
      
      let fullText = '';
      
      for (let pageNum = 1; pageNum <= Math.min(pdf.numPages, 5); pageNum++) {
        try {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();
          
          const pageText = textContent.items
            .filter((item: any) => item.str && item.str.trim().length > 0)
            .map((item: any) => item.str)
            .join(' ');
          
          if (pageText.trim()) {
            fullText += pageText + '\n\n';
          }
        } catch (pageError) {
          console.warn(`Error extracting text from page ${pageNum}:`, pageError);
        }
      }
      
      fullText = fullText
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s.,!?;:'"()\-\$%@#]/g, ' ')
        .trim();
      
      if (fullText.length < 50) {
        throw new Error('Could not extract meaningful text from PDF');
      }
      
      return fullText.slice(0, 4000);
      
    } catch (error: any) {
      console.error('PDF extraction error:', error);
      throw new Error(`Failed to extract text from PDF: ${error.message}`);
    }
  };

  const generateTermSheet = async (text: string): Promise<InvestmentTermSheet> => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    return {
      investors: `**PARTIES TO THIS INVESTMENT**

Lead Investor: [Venture Fund Name]
- Fund Size: $[Amount]
- Investment Focus: [Sector]
- Contact: [Partner Name]

Company: [Portfolio Company Name]
- Founded: [Year]
- Headquarters: [Location]
- CEO: [Name]

Co-Investors: [Additional Investors]

**Source:** Extracted from uploaded investment document
**Document Preview:** "${text.slice(0, 200)}..."`,

      companyOverview: `**COMPANY & OPPORTUNITY**

Company Description:
• Business Model: [Description]
• Market Opportunity: [Size & TAM]
• Stage: [Seed/Series A/B/C]
• Traction: Key metrics and milestones

Investment Highlights:
• Strong founding team with domain expertise
• Proven market demand and customer validation
• Scalable technology platform
• Clear path to profitability

**Based on extracted content:** ${text.slice(0, 300)}...`,

      investmentTerms: `**INVESTMENT TERMS**

Financing Amount: $[Amount]
Pre-Money Valuation: $[Amount]
Post-Money Valuation: $[Amount]

Security Type: [Preferred Stock/SAFE/Convertible Note]
Price Per Share: $[Amount]
Number of Shares: [Count]

Key Terms:
• Liquidation Preference: [X]x non-participating
• Anti-dilution Protection: Weighted average
• Voting Rights: [Details]
• Board Seats: [Number and allocation]

**From Document:** Investment structure extracted from uploaded term sheet.
**Content:** "${text.slice(200, 500)}..."`,

      governance: `**GOVERNANCE & CONTROL**

Board Composition:
• [X] Founder seats
• [X] Investor seats  
• [X] Independent seats

Protective Provisions:
• Approval required for major decisions
• Sale or merger of company
• Issuance of senior securities
• Changes to certificate of incorporation

Information Rights:
• Monthly financial statements
• Annual audited financials
• Quarterly board meetings
• Annual budget approval

**Extracted from:** Your uploaded investment document`,

      closingConditions: `**CLOSING CONDITIONS & TIMELINE**

Conditions Precedent:
• Completion of due diligence
• Satisfactory legal review
• Board and shareholder approval
• No material adverse changes

Timeline:
• Term sheet acceptance: [Date]
• Due diligence period: [X] weeks
• Final documentation: [Date]
• Expected closing: [Date]

Use of Proceeds:
• Product development: [%]
• Sales & marketing: [%]
• Operations & hiring: [%]
• Working capital: [%]

**Note:** Specific terms referenced in uploaded document.
**Source Content:** "${text.slice(100, 400)}..."`,

      signatureLines: `**EXECUTION AND SIGNATURES**

By signing below, the parties agree to proceed with this investment on the terms outlined:

**LEAD INVESTOR:**                    **COMPANY:**

_________________________        _________________________
Print Name: [Partner Name]         Print Name: [CEO Name]
Title: Managing Partner            Title: Chief Executive Officer
Date: _______________            Date: _______________
Signature: _______________       Signature: _______________


**CO-INVESTOR (if applicable):**

_________________________
Print Name: [Name]
Title: [Title]
Date: _______________
Signature: _______________

**Document Reference:** This term sheet was generated from uploaded document: "${text.slice(0, 150)}..."`
    };
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    if (!uploadedFile.type.includes('pdf')) {
      toast({
        title: "Invalid File",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    setFile(uploadedFile);
    setIsProcessing(true);
    setProgress(0);

    try {
      setProgress(20);
      const text = await extractTextFromPDF(uploadedFile);
      setExtractedText(text);
      
      setProgress(50);
      const generatedTermSheet = await generateTermSheet(text);
      
      setProgress(100);
      setTermSheet(generatedTermSheet);
      
      toast({
        title: "Success!",
        description: "Term sheet generated successfully",
      });
    } catch (error: any) {
      console.error('Error processing file:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process PDF",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!termSheet) return;

    const pdf = new jsPDF();
    let yPosition = 20;

    pdf.setFontSize(18);
    pdf.text('Investment Term Sheet', 20, yPosition);
    yPosition += 15;

    const sections = [
      { title: 'Investors & Company', content: termSheet.investors },
      { title: 'Company Overview', content: termSheet.companyOverview },
      { title: 'Investment Terms', content: termSheet.investmentTerms },
      { title: 'Governance', content: termSheet.governance },
      { title: 'Closing Conditions', content: termSheet.closingConditions },
      { title: 'Signatures', content: termSheet.signatureLines }
    ];

    sections.forEach(section => {
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.text(section.title, 20, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(section.content, 170);
      pdf.text(lines, 20, yPosition);
      yPosition += (lines.length * 5) + 10;
    });

    pdf.save('investment-term-sheet.pdf');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload Investment Document</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <Input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload" className="cursor-pointer">
                <Button variant="outline" asChild>
                  <span>
                    <FileText className="mr-2 h-4 w-4" />
                    Select PDF Document
                  </span>
                </Button>
              </label>
              {file && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {isProcessing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Processing document...</span>
                </div>
                <Progress value={progress} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {termSheet && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Generated Term Sheet</CardTitle>
              <Button onClick={handleDownloadPDF}>
                <Download className="mr-2 h-4 w-4" />
                Download PDF
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(termSheet).map(([key, value]) => (
                <div key={key} className="space-y-2">
                  <label className="text-sm font-medium capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </label>
                  <Textarea
                    value={value}
                    onChange={(e) => setTermSheet({...termSheet, [key]: e.target.value})}
                    rows={6}
                    className="font-mono text-sm"
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
